import { useState, useEffect } from 'react';
import {
    Box, Heading, Flex, Button, HStack, VStack, Text, Badge, Select, Input, Divider, SimpleGrid,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer, useToast, Spinner, Center, IconButton, Tooltip,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, useDisclosure,
    useBreakpointValue,
} from '@chakra-ui/react';
import { FiPlus, FiEdit, FiSend, FiArchive, FiRefreshCw, FiBarChart2, FiTrash2, FiBell, FiSearch } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { formatDateTime } from '../utils/dateUtils';

const STATUS_META = {
    draft: { label: '草稿', color: 'gray' },
    scheduled: { label: '預約', color: 'purple' },
    published: { label: '已發布', color: 'green' },
    archived: { label: '已下架', color: 'gray' },
};
const DEPT_LABEL = { AfterSchool: '全科班', English: '英文部', Talent: '才藝部' };

const targetsSummary = (targets = []) => {
    if (!targets || !targets.length) return '—';
    if (targets.some((t) => t.target_type === 'all')) return '全校';
    const grades = targets.filter((t) => t.target_type === 'grade').map((t) => t.target_value);
    const depts = targets.filter((t) => t.target_type === 'department').map((t) => DEPT_LABEL[t.target_value] || t.target_value);
    const classes = targets.filter((t) => t.target_type === 'class');
    const parts = [];
    if (grades.length) parts.push(`年級:${grades.join(',')}`);
    if (depts.length) parts.push(`部門:${depts.join(',')}`);
    if (classes.length) parts.push(`班級×${classes.length}`);
    return parts.join('；') || '—';
};

// 統一用 dateUtils 強制以 Asia/Taipei 顯示
const fmt = formatDateTime;

const AnnouncementsAdminPage = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const isMobile = useBreakpointValue({ base: true, md: false });
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    const [targetType, setTargetType] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [filterOpen, setFilterOpen] = useState(false);

    const { isOpen: statsOpen, onOpen: openStats, onClose: closeStats } = useDisclosure();
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsTitle, setStatsTitle] = useState('');
    const [statsAnnId, setStatsAnnId] = useState(null);
    const [reminding, setReminding] = useState(false);

    const openReadStats = (a) => {
        setStatsTitle(a.title);
        setStatsAnnId(a.id);
        setStats(null);
        setStatsLoading(true);
        openStats();
        authAPI.announcements.readStats(a.id)
            .then((res) => setStats(res.data))
            .catch(() => toast({ title: '統計載入失敗', status: 'error', duration: 3000 }))
            .finally(() => setStatsLoading(false));
    };

    const handleRemindUnread = async (unreadCount) => {
        if (!statsAnnId || unreadCount === 0) return;
        if (!window.confirm(`確定要發送提醒給 ${unreadCount} 位未讀家長嗎？`)) return;
        setReminding(true);
        try {
            const res = await authAPI.announcements.remindUnread(statsAnnId);
            toast({ title: `已發送提醒給 ${res.data.sent} 位家長`, status: 'success', duration: 3000 });
        } catch (e) {
            toast({ title: '發送失敗', description: e?.response?.data?.detail || '', status: 'error', duration: 3000 });
        } finally {
            setReminding(false);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (targetType) params.target_type = targetType;
            if (search.trim()) params.search = search.trim();
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            const res = await authAPI.announcements.listAdmin(params);
            setItems(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            toast({ title: '載入失敗', status: 'error', duration: 3000 });
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const t = setTimeout(fetchData, 500); // 篩選變動 debounce（含關鍵字打字）
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, search, targetType, startDate, endDate]);

    const doAction = async (id, action, label) => {
        setBusyId(id);
        try {
            await authAPI.announcements[action](id);
            toast({ title: `${label}成功`, status: 'success', duration: 2000 });
            fetchData();
        } catch (e) {
            toast({ title: `${label}失敗`, description: e?.response?.data?.detail || '', status: 'error', duration: 3000 });
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = (a) => {
        if (!window.confirm(`確定要刪除草稿「${a.title}」嗎？此動作無法復原。`)) return;
        doAction(a.id, 'delete', '刪除');
    };

    const clearFilters = () => { setSearch(''); setTargetType(''); setStartDate(''); setEndDate(''); };
    const hasActiveFilters = !!(search || targetType || startDate || endDate);

    const statsModal = (
        <Modal isOpen={statsOpen} onClose={closeStats} size="lg" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader noOfLines={1}>已讀統計 · {statsTitle}</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    {statsLoading ? (
                        <Center py={8}><Spinner /></Center>
                    ) : stats ? (
                        (() => {
                            const unread = (stats.students || []).filter((s) => !s.is_read);
                            const read = (stats.students || []).filter((s) => s.is_read);
                            return (
                                <VStack align="stretch" spacing={4}>
                                    <Text fontWeight="bold">已讀 {stats.read_count} / 應讀 {stats.target_count} 位學生</Text>
                                    <Divider />
                                    <Box>
                                        <Flex justify="space-between" align="center" mb={2}>
                                            <Text fontSize="sm" fontWeight="bold" color="red.500">
                                                未讀（{unread.length}）
                                            </Text>
                                            {unread.length > 0 && (
                                                <Button
                                                    size="xs"
                                                    colorScheme="orange"
                                                    leftIcon={<FiBell />}
                                                    isLoading={reminding}
                                                    onClick={() => handleRemindUnread(unread.length)}
                                                >
                                                    催讀未讀家長
                                                </Button>
                                            )}
                                        </Flex>
                                        {unread.length === 0 ? (
                                            <Text fontSize="sm" color="var(--fg-muted)">— 全部已讀 —</Text>
                                        ) : (
                                            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                                                {unread.map((s) => (
                                                    <HStack key={s.student_id} bg="red.50" px={3} py={1.5} borderRadius="md">
                                                        <Text fontSize="sm" isTruncated>
                                                            {s.student_name}
                                                            {s.class_name && <Text as="span" color="var(--fg-weak)"> ({s.class_name})</Text>}
                                                        </Text>
                                                    </HStack>
                                                ))}
                                            </SimpleGrid>
                                        )}
                                    </Box>
                                    <Divider />
                                    <Box>
                                        <Text fontSize="sm" fontWeight="bold" color="green.600" mb={2}>
                                            已讀（{read.length}）
                                        </Text>
                                        {read.length === 0 ? (
                                            <Text fontSize="sm" color="var(--fg-muted)">— 尚無人已讀 —</Text>
                                        ) : (
                                            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                                                {read.map((s) => (
                                                    <HStack key={s.student_id} justify="space-between" bg="green.50" px={3} py={1.5} borderRadius="md">
                                                        <Text fontSize="sm" isTruncated>
                                                            {s.student_name}
                                                            {s.class_name && <Text as="span" color="var(--fg-weak)"> ({s.class_name})</Text>}
                                                        </Text>
                                                        <Text fontSize="xs" color="var(--fg-muted)" whiteSpace="nowrap" ml={2}>{fmt(s.read_at)}</Text>
                                                    </HStack>
                                                ))}
                                            </SimpleGrid>
                                        )}
                                    </Box>
                                </VStack>
                            );
                        })()
                    ) : (
                        <Text color="var(--fg-muted)">載入失敗</Text>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );

    if (isMobile) {
        return (
            <>
            <Box p={3}>
                <Flex gap={2} mb={3}>
                    <Button flex="1" size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={() => navigate('/announcements/new')}>新增公告</Button>
                    <Button flex="1" size="sm" leftIcon={<FiSearch />} variant="outline" onClick={() => setFilterOpen((v) => !v)}>尋找公告</Button>
                </Flex>

                {filterOpen && (
                    <VStack align="stretch" spacing={2} mb={3} bg="white" p={3} borderRadius="lg" border="1px solid" borderColor="gray.200">
                        <Select size="sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} placeholder="全部狀態（不含下架）">
                            <option value="draft">草稿</option>
                            <option value="scheduled">預約</option>
                            <option value="published">已發布</option>
                            <option value="archived">已下架</option>
                        </Select>
                        <Select size="sm" value={targetType} onChange={(e) => setTargetType(e.target.value)} placeholder="全部對象">
                            <option value="all">全校</option>
                            <option value="grade">年級</option>
                            <option value="class">班級</option>
                            <option value="department">部門</option>
                        </Select>
                        <Input size="sm" placeholder="搜尋標題…" value={search} onChange={(e) => setSearch(e.target.value)} />
                        <HStack spacing={1}>
                            <Input size="sm" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                            <Text fontSize="sm" color="gray.400">~</Text>
                            <Input size="sm" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        </HStack>
                        {hasActiveFilters && (
                            <Button size="xs" variant="ghost" alignSelf="flex-start" onClick={clearFilters}>清除篩選</Button>
                        )}
                    </VStack>
                )}

                {loading ? (
                    <Center py={16}><Spinner size="lg" /></Center>
                ) : items.length === 0 ? (
                    // 空狀態文字沒有卡片承載、直接落在外殼畫布上；本頁在老師 PWA 也會渲染
                    //（畫布是霧灰橄欖，gray.500 僅 2.29 對比）。本檔其他地方已用 --fg-*，這裡補齊。
                    <Center py={16}><Text color="var(--fg-muted)">目前沒有公告</Text></Center>
                ) : (
                    <VStack align="stretch" spacing={2}>
                        {items.map((a) => {
                            const sm = STATUS_META[a.status] || { label: a.status, color: 'gray' };
                            return (
                                <Box
                                    key={a.id} bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200"
                                    p={3} cursor="pointer" onClick={() => navigate(`/announcements/${a.id}/edit`)}
                                >
                                    {/* 第一行：緊急/置頂 + 標題 */}
                                    <HStack spacing={1.5} mb={1}>
                                        {a.is_emergency && <Badge colorScheme="red" fontSize="10px">緊急</Badge>}
                                        {a.is_pinned && <Badge colorScheme="orange" fontSize="10px">置頂</Badge>}
                                        <Text fontWeight="medium" fontSize="sm" noOfLines={1}>{a.title}</Text>
                                    </HStack>
                                    {/* 第二行：狀態 + 對象 + 已讀 + 時間 */}
                                    <Flex justify="space-between" align="center" gap={2}>
                                        <HStack spacing={2} minW={0}>
                                            <Badge colorScheme={sm.color} fontSize="10px">{sm.label}</Badge>
                                            <Text fontSize="xs" color="gray.500" noOfLines={1}>{targetsSummary(a.targets)}</Text>
                                        </HStack>
                                        <Text fontSize="xs" color="gray.400" whiteSpace="nowrap" flexShrink={0}>
                                            已讀{a.read_count ?? 0}・{fmt(a.published_at || a.publish_at)}
                                        </Text>
                                    </Flex>
                                    {/* 動作列 */}
                                    <HStack spacing={1} justify="flex-end" mt={1.5} onClick={(e) => e.stopPropagation()}>
                                        <IconButton aria-label="stats" size="xs" variant="ghost" icon={<FiBarChart2 />} onClick={() => openReadStats(a)} />
                                        {a.status !== 'published' && (
                                            <IconButton aria-label="publish" size="xs" colorScheme="green" icon={<FiSend />} isLoading={busyId === a.id} onClick={() => doAction(a.id, 'publish', '發布')} />
                                        )}
                                        {a.status === 'draft' && (
                                            <IconButton aria-label="delete" size="xs" colorScheme="red" variant="ghost" icon={<FiTrash2 />} isLoading={busyId === a.id} onClick={() => handleDelete(a)} />
                                        )}
                                        {a.status === 'published' && (
                                            <>
                                                <IconButton aria-label="repush" size="xs" colorScheme="blue" icon={<FiRefreshCw />} isLoading={busyId === a.id} onClick={() => doAction(a.id, 'repush', '重新推播')} />
                                                <IconButton aria-label="archive" size="xs" icon={<FiArchive />} isLoading={busyId === a.id} onClick={() => doAction(a.id, 'archive', '下架')} />
                                            </>
                                        )}
                                    </HStack>
                                </Box>
                            );
                        })}
                    </VStack>
                )}
            </Box>
            {statsModal}
            </>
        );
    }

    return (
        <>
        <Box p={{ base: 4, md: 6 }}>
            <Flex justify="space-between" align="center" mb={5} wrap="wrap" gap={3}>
                <Heading size="lg">校園公告</Heading>
                <HStack>
                    <Select size="sm" w="150px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} placeholder="全部（不含下架）">
                        <option value="draft">草稿</option>
                        <option value="scheduled">預約</option>
                        <option value="published">已發布</option>
                        <option value="archived">已下架</option>
                    </Select>
                    <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={() => navigate('/announcements/new')}>新增公告</Button>
                </HStack>
            </Flex>

            {/* 篩選列：對象 / 標題關鍵字 / 日期範圍 */}
            <Flex mb={4} gap={2} wrap="wrap" align="center">
                <Select size="sm" w="130px" value={targetType} onChange={(e) => setTargetType(e.target.value)} placeholder="全部對象">
                    <option value="all">全校</option>
                    <option value="grade">年級</option>
                    <option value="class">班級</option>
                    <option value="department">部門</option>
                </Select>
                <Input size="sm" w="180px" placeholder="搜尋標題…" value={search} onChange={(e) => setSearch(e.target.value)} />
                <HStack spacing={1}>
                    <Input size="sm" w="150px" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    <Text fontSize="sm" color="gray.400">~</Text>
                    <Input size="sm" w="150px" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </HStack>
                {hasActiveFilters && (
                    <Button size="sm" variant="ghost" onClick={clearFilters}>
                        清除篩選
                    </Button>
                )}
            </Flex>

            {loading ? (
                <Center py={20}><Spinner size="lg" /></Center>
            ) : (
                <TableContainer bg="white" borderRadius="lg" boxShadow="sm">
                    <Table size="sm">
                        <Thead bg="gray.50">
                            <Tr>
                                <Th>標題</Th><Th>狀態</Th><Th>對象</Th><Th>已讀</Th><Th>發布時間</Th><Th>操作</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {items.length === 0 && (
                                <Tr><Td colSpan={6}><Center py={10} color="gray.500">目前沒有公告</Center></Td></Tr>
                            )}
                            {items.map((a) => {
                                const sm = STATUS_META[a.status] || { label: a.status, color: 'gray' };
                                return (
                                    <Tr key={a.id}>
                                        <Td maxW="280px" whiteSpace="normal">
                                            <HStack spacing={2}>
                                                {a.is_emergency && <Badge colorScheme="red">緊急</Badge>}
                                                {a.is_pinned && <Badge colorScheme="orange">置頂</Badge>}
                                                <Text fontWeight="medium" noOfLines={1}>{a.title}</Text>
                                            </HStack>
                                        </Td>
                                        <Td><Badge colorScheme={sm.color}>{sm.label}</Badge></Td>
                                        <Td><Text fontSize="sm" color="gray.600">{targetsSummary(a.targets)}</Text></Td>
                                        <Td><Text fontSize="sm" color="gray.600">{a.read_count ?? 0}</Text></Td>
                                        <Td><Text fontSize="sm" color="gray.600">{fmt(a.published_at || a.publish_at)}</Text></Td>
                                        <Td>
                                            <HStack spacing={1}>
                                                <Tooltip label="已讀統計">
                                                    <IconButton aria-label="stats" size="sm" variant="ghost" icon={<FiBarChart2 />} onClick={() => openReadStats(a)} />
                                                </Tooltip>
                                                <Tooltip label="編輯">
                                                    <IconButton aria-label="edit" size="sm" icon={<FiEdit />} onClick={() => navigate(`/announcements/${a.id}/edit`)} />
                                                </Tooltip>
                                                {a.status !== 'published' && (
                                                    <Tooltip label="發布">
                                                        <IconButton aria-label="publish" size="sm" colorScheme="green" icon={<FiSend />} isLoading={busyId === a.id} onClick={() => doAction(a.id, 'publish', '發布')} />
                                                    </Tooltip>
                                                )}
                                                {a.status === 'draft' && (
                                                    <Tooltip label="刪除草稿">
                                                        <IconButton aria-label="delete" size="sm" colorScheme="red" variant="ghost" icon={<FiTrash2 />} isLoading={busyId === a.id} onClick={() => handleDelete(a)} />
                                                    </Tooltip>
                                                )}
                                                {a.status === 'published' && (
                                                    <>
                                                        <Tooltip label="重新推播">
                                                            <IconButton aria-label="repush" size="sm" colorScheme="blue" icon={<FiRefreshCw />} isLoading={busyId === a.id} onClick={() => doAction(a.id, 'repush', '重新推播')} />
                                                        </Tooltip>
                                                        <Tooltip label="下架">
                                                            <IconButton aria-label="archive" size="sm" icon={<FiArchive />} isLoading={busyId === a.id} onClick={() => doAction(a.id, 'archive', '下架')} />
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </HStack>
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </TableContainer>
            )}
        </Box>
        {statsModal}
        </>
    );
};

export default AnnouncementsAdminPage;
